import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * The current tab, whichever mode drew it: Base UI marks a selected tab `data-active`, a pressed
 * toggle `data-pressed`, and a link is current by `aria-current`. Reading the DOM instead of the
 * value means panels, filter and navigation share one measurement, and an uncontrolled Base UI
 * root needs no mirror of its own state.
 */
const CURRENT = '[data-active], [data-pressed], [aria-current="page"]';

/** Width of a scroll-edge fade, `--spacing * 10`. A tab is scrolled clear of it, not under it. */
const FADE_PX = 40;

/** The scrollport's own padding, which holds the focus ring and shifts the track's origin. */
const SCROLLER_PADDING_PX = 4;

type Modality = 'keyboard' | 'pointer' | null;

const prefersReducedMotion = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

/**
 * Measures the strip: where the indicator goes, whether the edges overflow, and which tab to bring
 * into view. One implementation for all three modes.
 *
 * WHEN THE INDICATOR ANIMATES. Only when the current tab changed AND a pointer made the change. It
 * snaps on the first paint, on a resize or a label change (the tab did not change, its box did), and
 * after any key press, because keyboard-driven movement never animates. Reduced motion is handled in
 * the recipe, where `data-animate` only transitions under `motion-safe:`.
 *
 * Imperative on purpose: the indicator's position is written straight to its style, so a resize
 * repositions it without a React render, and React never sees a prop to reset.
 */
export const useTabsStrip = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const indicatorRef = useRef<HTMLSpanElement>(null);
  const modality = useRef<Modality>(null);
  const [edges, setEdges] = useState({ start: false, end: false });

  const onKeyDownCapture = useCallback(() => {
    modality.current = 'keyboard';
  }, []);

  const onPointerDownCapture = useCallback(() => {
    modality.current = 'pointer';
  }, []);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const track = trackRef.current;
    const indicator = indicatorRef.current;

    if (!scroller || !track || !indicator) {
      return undefined;
    }

    let previous: HTMLElement | null = null;

    const measureEdges = () => {
      const start = scroller.scrollLeft > 0;
      // Rounded up, as the Dialog's fades are: a fractional scroll position otherwise leaves the end
      // fade showing a pixel short of the end, for ever.
      const end = Math.ceil(scroller.scrollLeft + scroller.clientWidth) < scroller.scrollWidth;

      setEdges((current) => (current.start === start && current.end === end ? current : { start, end }));
    };

    const bringIntoView = (tab: HTMLElement, animate: boolean) => {
      if (scroller.scrollWidth <= scroller.clientWidth) {
        return;
      }

      const left = tab.offsetLeft + SCROLLER_PADDING_PX;
      const right = left + tab.offsetWidth;
      const viewStart = scroller.scrollLeft;
      const viewEnd = viewStart + scroller.clientWidth;
      let target: number | null = null;

      if (left - FADE_PX < viewStart) {
        target = Math.max(0, left - FADE_PX);
      } else if (right + FADE_PX > viewEnd) {
        target = right + FADE_PX - scroller.clientWidth;
      }

      if (target !== null) {
        scroller.scrollTo({ left: target, behavior: animate && !prefersReducedMotion() ? 'smooth' : 'auto' });
      }
    };

    const measure = () => {
      const tab = track.querySelector<HTMLElement>(CURRENT);

      if (!tab) {
        delete indicator.dataset.ready;
        delete indicator.dataset.animate;
        previous = null;
        measureEdges();

        return;
      }

      const changed = tab !== previous;
      const animate = changed && previous !== null && modality.current !== 'keyboard';

      if (animate) {
        indicator.dataset.animate = '';
      } else {
        delete indicator.dataset.animate;
      }

      indicator.style.setProperty('--ds-tab-x', `${tab.offsetLeft}px`);
      indicator.style.setProperty('--ds-tab-sx', String(tab.offsetWidth));
      indicator.dataset.ready = '';

      // Written only on a change: `data-disabled` is one of the attributes the observer below is
      // watching, and this element is inside what it watches, so an unconditional write would queue
      // a record for itself and measure for ever.
      const disabled = tab.hasAttribute('data-disabled');

      if (disabled !== indicator.hasAttribute('data-disabled')) {
        if (disabled) {
          indicator.dataset.disabled = '';
        } else {
          delete indicator.dataset.disabled;
        }
      }

      if (changed) {
        bringIntoView(tab, animate);
      }

      previous = tab;
      measureEdges();
    };

    measure();

    const resize = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    const observeTabs = () => {
      resize?.disconnect();
      resize?.observe(track);
      resize?.observe(scroller);
      track.querySelectorAll('.ds-tab').forEach((tab) => resize?.observe(tab));
    };

    observeTabs();

    const mutations = new MutationObserver((records) => {
      if (records.some((record) => record.type === 'childList')) {
        observeTabs();
      }

      measure();
    });

    mutations.observe(track, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['data-active', 'data-pressed', 'aria-current', 'data-disabled'],
    });
    scroller.addEventListener('scroll', measureEdges, { passive: true });

    return () => {
      resize?.disconnect();
      mutations.disconnect();
      scroller.removeEventListener('scroll', measureEdges);
    };
  }, []);

  return { edges, indicatorRef, onKeyDownCapture, onPointerDownCapture, scrollerRef, trackRef };
};
